import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Button } from "@vb/ui";

/**
 * Primary site nav.
 *
 * Order matters — buyers scan left-to-right and we want the first three items
 * to answer the most common pre-sales questions:
 *   Games (what do you have?), Pricing (how much?), Integration (how hard?).
 *
 * "Pricing" anchors to the homepage pricing section. The anchor lands at top
 * of /home if no `id="pricing"` exists yet — harmless but ideally Home.tsx
 * gets that id wired to its <Section> at some point.
 */

type NavItem = { label: string; to: string };

const NAV_ITEMS: NavItem[] = [
  { label: "Games", to: "/games" },
  { label: "Commission", to: "/commission" },
  { label: "Pricing", to: "/#pricing" },
  { label: "Integration", to: "/integration" },
  { label: "Docs", to: "/docs" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

// Items surfaced inside the mobile hamburger panel. Keeps the desktop CTA
// row available to thumbs without forcing them to scroll horizontally.
const MOBILE_EXTRA: NavItem[] = [
  { label: "Try sandbox", to: "/sandbox" },
  { label: "FAQ", to: "/faq" },
  { label: "Status", to: "/status" },
];

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        background: "rgba(10,14,26,0.9)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderBottom: "1px solid var(--line)",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <img src="/logo-mark.svg" alt="" style={{ height: 28, width: 28 }} />
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 16,
              letterSpacing: "0.06em",
              color: "var(--fg-1)",
              lineHeight: 1,
            }}
          >
            VAULT BREAKER
            <span style={{ color: "var(--fg-3)", marginLeft: 8, fontSize: 13, letterSpacing: "0.2em" }}>
              STUDIOS
            </span>
          </span>
        </Link>

        <nav
          className="vb-nav-desktop"
          style={{
            display: "flex",
            gap: 6,
            marginLeft: 32,
            flex: 1,
          }}
        >
          {NAV_ITEMS.map((item) => renderDesktopNavItem(item))}
        </nav>

        <div style={{ display: "flex", gap: 8, marginLeft: "auto", alignItems: "center" }}>
          <Link to="/sandbox" style={{ textDecoration: "none" }} className="vb-hide-mobile">
            <Button variant="gold" style={{ padding: "9px 14px", fontSize: 13 }}>
              TRY SANDBOX →
            </Button>
          </Link>
          <Link to="/contact" style={{ textDecoration: "none" }} className="vb-hide-mobile">
            <Button variant="ghost" style={{ padding: "9px 14px", fontSize: 13 }}>
              Contact sales
            </Button>
          </Link>
          <button
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            className="vb-hamburger"
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              background: "transparent",
              border: "1px solid var(--line)",
              color: "var(--fg-1)",
              width: 36,
              height: 36,
              borderRadius: 8,
              cursor: "pointer",
              padding: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </svg>
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div
          style={{
            borderTop: "1px solid var(--line)",
            background: "var(--bg-1)",
            padding: "8px 20px 16px",
          }}
          onClick={() => setMenuOpen(false)}
        >
          {[...NAV_ITEMS, ...MOBILE_EXTRA].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              style={{
                display: "block",
                padding: "12px 8px",
                color: location.pathname === item.to ? "var(--fg-1)" : "var(--fg-2)",
                textDecoration: "none",
                fontSize: 15,
                fontWeight: 500,
                borderBottom: "1px solid var(--line)",
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </header>
  );
}

/**
 * Desktop nav item renderer. NavLink gives us the active-state styling for
 * normal routes, but anchored links (e.g. `/#pricing`) need a plain Link —
 * NavLink's active match would never resolve since the URL fragment isn't
 * part of pathname matching.
 */
function renderDesktopNavItem(item: NavItem) {
  const sharedStyle = {
    padding: "8px 12px",
    borderRadius: 8,
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 500,
  } as const;

  if (item.to.includes("#")) {
    return (
      <Link
        key={item.to}
        to={item.to}
        style={{ ...sharedStyle, color: "var(--fg-2)", background: "transparent" }}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <NavLink
      key={item.to}
      to={item.to}
      style={({ isActive }) => ({
        ...sharedStyle,
        color: isActive ? "var(--fg-1)" : "var(--fg-2)",
        background: isActive ? "var(--bg-3)" : "transparent",
      })}
    >
      {item.label}
    </NavLink>
  );
}
